import { ClickAwayListener, Popper, styled } from '@mui/material'
import LinkIcon from '@mui/icons-material/LinkRounded'
import { memo, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import {
  Menu,
  MenuItem,
  MenuItemText,
  PopperRoot,
} from '../../../../components/menu'
import { CardContent } from '../../Kanban'

type Props = {
  content: CardContent
  anchorPosition: { left: number; top: number } | null
  onClose: () => void
}

const createVirtualAnchorEl = (position: { left: number; top: number }) => ({
  getBoundingClientRect: () => ({
    top: position.top,
    left: position.left,
    bottom: position.top,
    right: position.left,
    width: 0,
    height: 0,
    x: position.left,
    y: position.top,
    toJSON: () => ({}),
  }),
})

export const CardContextMenu = memo(
  ({ content, anchorPosition, onClose }: Props) => {
    const intl = useIntl()

    const handleCopyUrl = useCallback(() => {
      if (!content.code) return
      const url = `${window.location.origin}/wbsItem/${content.code}`
      navigator.clipboard.writeText(url)
      onClose()
    }, [content.code, onClose])

    const handleCopyUrlAndName = useCallback(() => {
      if (!content.code) return
      const text = `${content.displayName}\n${window.location.origin}/wbsItem/${content.code}`
      navigator.clipboard.writeText(text)
      onClose()
    }, [content.code, content.displayName, onClose])

    const virtualAnchorEl = useMemo(() => {
      if (!anchorPosition) return null
      return createVirtualAnchorEl(anchorPosition)
    }, [anchorPosition])

    return (
      <Popper
        open={virtualAnchorEl !== null}
        anchorEl={virtualAnchorEl}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={onClose}>
          <PopperRoot>
            <Menu>
              <MenuItem onClick={handleCopyUrl}>
                <StyledLinkIcon />
                <MenuItemText>
                  {intl.formatMessage({ id: 'bulksheet.contextMenu.url.copy' })}
                </MenuItemText>
              </MenuItem>
              <MenuItem onClick={handleCopyUrlAndName}>
                <StyledLinkIcon />
                <MenuItemText>
                  {intl.formatMessage({
                    id: 'bulksheet.contextMenu.urlAndName.copy',
                  })}
                </MenuItemText>
              </MenuItem>
            </Menu>
          </PopperRoot>
        </ClickAwayListener>
      </Popper>
    )
  }
)

const StyledLinkIcon = styled(LinkIcon)({
  fontSize: 16,
  transform: 'rotate(-45deg)',
})
